<template>
    <div id="client-message-page" v-if="title && body">
        <checkout-header
            :categoryFilter="{display: false}"
            :shoppingBag="{display: false}">
        </checkout-header>
        <div id="message-container" class="flex flex-center">
            <error-state-icon :iconType="iconToShow"></error-state-icon>
            <h2 id="message-title" class="bold">{{ title }}</h2>
            <p id="message-body">{{ body }}</p>
            <v-button
                v-if="content && content.clientMessages[clientMessageKey].button_text"
                position="center"
                mobile="12"
                tablet="12"
                desktop="12"
                styling="primary"
                textAlign="center"
                :disabled="false"
                @onClickEvent="navigateOut()"
                >
                <template v-slot:content>
                    <span>
                        {{ buttonText }}
                    </span>
                </template>
            </v-button>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import checkoutHeader from '../../checkouts/layouts/header';
    import ErrorStateIcon from '../error-state-icon';
    import vButton from '../button';
    import {snakeCase, uncamelize} from '../../../utils';
    import { analyticsTrack } from '../../../plugins/segment';

    export default {
        components: {checkoutHeader, ErrorStateIcon, vButton},

        data() {
            return {
                prevRoute: null,
                contentBasedOnRoute: {
                    '/webstore': 'webstoreContent',
                    '/small-fleets': 'webstoreContent',
                    '/review-cart': 'webstoreContent',
                    '/checkout/shipping': 'checkoutContent',
                    '/checkout/review': 'checkoutContent',
                    '/checkout/payment': 'checkoutContent',
                    '/checkout/confirm': 'checkoutContent',
                    '/checkout/pm-update': 'checkoutContent',
                    '/checkout/payment-update': 'checkoutContent',
                },
                shippingPageErrors: [
                    'tax_error',
                    'shipping_and_handling_error',
                    'session_expired'
                ],
            };
        },

        computed: {
            ...mapGetters('checkout', [
                'accountId',
                'opptyId',
                'isSelfServiced',
                'checkoutType',
                'smallFleetOpportunity',
                'currencyIsoCode',
                'isExistingCustomer'
            ]),

            ...mapGetters('content', [
                'webstoreContent',
                'checkoutContent',
            ]),

            buttonText() {
                if (this.content) {
                    if (this.isClientMessageKeyInContent) {
                        return this.content.clientMessages[this.clientMessageKey].button_text;
                    } else {
                        return this.content.clientMessages.unable_to_checkout.button_text;
                    }
                } else {
                    return 'Go back to cloud dashboard'
                }
            },

            iconToShow() {
                return ['order_confirmed', 'license_purchase_request'].includes(this.clientMessageKey) ? 'success' : 'error';
            },

            clientMessageKey() {
                return snakeCase(uncamelize(this.$route.query.type));
            },

            content() {
                let contentfulEntry = this[this.contentBasedOnRoute[this.prevRoute]];
                return contentfulEntry && 'clientMessages' in contentfulEntry ? contentfulEntry : null;
            },

            isClientMessageKeyInContent() {
                return this.content ? this.clientMessageKey in this.content.clientMessages : false;
            },

            title() {
                if (this.content) {
                    if (this.isClientMessageKeyInContent) {
                        return this.content.clientMessages[this.clientMessageKey].title;
                    } else {
                        return this.content.clientMessages.unable_to_checkout.title;
                    }
                } else {
                    if (this.clientMessageKey === 'order_confirmed') {
                        return "You're all set!";
                    } else if (this.clientMessageKey === 'error_creating_checkout') {
                        return 'Sorry, something went wrong';
                    } else if (this.clientMessageKey === 'incorrect_stripe_id') {
                        return 'Currency Mismatch Error';
                    } else {
                        return 'Unable to Checkout';
                    }
                }
            },

            body() {
                if (this.content) {
                    if (this.isClientMessageKeyInContent) {
                        return this.content.clientMessages[this.clientMessageKey].body;
                    } else {
                        return this.content.clientMessages.unable_to_checkout.body;
                    }
                } else {
                    if (this.clientMessageKey === 'error_creating_checkout') {
                        return 'Please try again after a few minutes. Thank you for your patience.';
                    } else if (this.clientMessageKey === 'incorrect_stripe_id') {
                        return 'Most likely, your account had its\' currency change after a previous purchase, causing confusion within our systems. Your Account Executive has been alerted and should reach out shortly with a solution.';
                    } else if(this.clientMessageKey === 'order_confirmed'){
                        return 'You have already completed this order! Please reach out to your AE with any concerns. '
                    } 
                    else {
                        return 'Please try accesing this page in a new or incognito window. If that does not resolve the issue, contact your Account Representative for more details.';
                    }
                }
            },

        },

        methods: {
            navigateOut() {
                if (this.content.clientMessages[this.clientMessageKey].cta_url) {
                    let cta_url = this.content.clientMessages[this.clientMessageKey].cta_url;
                    if ("webstore" in cta_url) {
                        cta_url = this.isSelfServiced ? cta_url.webstore : cta_url.checkout;
                    }
                    cta_url = `${cta_url.replace('ACCOUNT_ID', this.accountId).replace('OPPTY_ID', this.opptyId)}`;
                    if (cta_url.includes('samsara.com')) {
                        return window.location.href = `https://${cta_url}`;
                    }
                    return window.location.href = cta_url;
                }
            }
        },

        beforeRouteEnter(to, from, next) {
            next(vm => {
                vm.prevRoute = from.path
            })
        },

        watch: {
            prevRoute() {
                if (this.iconToShow === "error") {
                    analyticsTrack("Error State Received", {
                        "client_message_key": this.clientMessageKey,
                        "prev_route": this.prevRoute,
                        "checkout_type": this.checkoutType,
                        "is_self_serviced": this.isSelfServiced,
                        "is_small_fleet": this.smallFleetOpportunity,
                        "currency_iso_code": this.currencyIsoCode,
                        "is_existing_customer": this.isExistingCustomer
                    });
                } else {
                    analyticsTrack("Success State Received", {
                        "client_message_key": this.clientMessageKey,
                        "prev_route": this.prevRoute,
                        "checkout_type": this.checkoutType,
                        "is_self_serviced": this.isSelfServiced,
                        "is_small_fleet": this.smallFleetOpportunity,
                        "currency_iso_code": this.currencyIsoCode,
                        "is_existing_customer": this.isExistingCustomer
                    });
                }
            }
        },
    }
</script>

<style scoped>

    .flex {
        display: flex;
    }

    .flex-center {
        align-items: center;
        justify-content: center;
    }

    #client-message-page {
        width: 100%;
        height: 100%;
        font-family: "Nitti Grotesk", sans-serif;
        font-style: normal;
        font-weight: normal;
        margin: 0 auto;
    }

    #message-container {
        text-align: center;
        width: 100%;
        height: 70%;
        position: fixed;
        flex-direction: column;
    }

    #message-title {
        margin: 10px;
        font-size: 40px;
    }

    #message-body {
        width: 75%;
        max-width: 650px;
        margin-top: 0px;
        margin-bottom: 10px;
        font-size: 20px;
    }

</style>